import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_afc4fae4-3e91-49c0-b442-1e22d639f2c6/artifacts/5tm3hua0_Logo%20PF%402x.png";

const navItems = [
  { label: 'Accueil', href: '#hero' },
  { label: 'Qui sommes-nous', href: '#about' },
  { label: 'Missions', href: '#missions' },
  { label: 'Écosystème', href: '#ecosystem' },
  { label: 'Contact', href: '#contact' }
];

const Navigation = ({ settings }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Determine active section
      const sections = navItems.map(item => item.href.substring(1));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => scrollToSection(e, '#hero')}
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Place Financière BFC" 
              className="h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                data-testid={`nav-link-${item.href.substring(1)}`}
                className={`relative text-sm font-medium font-inter tracking-wide uppercase transition-colors ${
                  activeSection === item.href.substring(1)
                    ? 'text-[#1e3a8a]'
                    : 'text-slate-600 hover:text-[#1e3a8a]'
                }`}
              >
                {item.label}
                {activeSection === item.href.substring(1) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#D4AF37]" />
                )}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={(e) => scrollToSection(e, '#contact')}
              data-testid="nav-contact-btn"
              className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-6 py-2 font-inter font-medium rounded-full transition-all duration-300 hover:shadow-lg"
            >
              Nous contacter
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-600 hover:text-[#1e3a8a]"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                data-testid={`mobile-nav-link-${item.href.substring(1)}`}
                className={`block py-3 px-4 rounded font-inter text-sm font-medium transition-colors ${
                  activeSection === item.href.substring(1)
                    ? 'bg-slate-50 text-[#1e3a8a]'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#1e3a8a]'
                }`}
              >
                {item.label}
              </a>
            ))}
            <Button
              onClick={(e) => scrollToSection(e, '#contact')}
              className="w-full mt-4 bg-[#1e3a8a] hover:bg-[#172554] text-white font-inter font-medium rounded-full"
              data-testid="mobile-contact-btn"
            >
              Nous contacter
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
